import { Grid } from "@mui/material";
import {
  EqTypography,
  EqQuestionBlockContainer,
  EqButton,
  EqHelpText,
} from "@equitable/equx";
import { ChangeEvent, FormEvent, useState, useEffect } from "react";
import { emailUtils } from "../utils/emailUtils";
import ReCAPTCHA from "react-google-recaptcha";
import { SubscribeTranslationValues } from "../types";
import { subscriberServices } from "../services/subscriberServices";
import { useNavigate } from "react-router-dom";
import { tokenServices } from "../services/tokenServices";

type SubscribeProps = {
  translations: SubscribeTranslationValues;
  lang: "en" | "fr";
};

const Subscribe = ({ translations, lang }: SubscribeProps) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailHelpText, setEmailHelpText] = useState<string>("");
  const [captchaHelpText, setCaptchaHelpText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [captcha, setCaptcha] = useState<boolean>(false);
  const [xsrfToken, setXsrfToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchToken = async () => {
      const res = await tokenServices.getToken();
      setXsrfToken(res.data.requestToken);
    };

    fetchToken();
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // filter out numeric values from first and last name fields
    if (name === "firstName" || name === "lastName") {
      if (value.match(/[0-9;]/)) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (emailError) {
      if (emailUtils.isValid(value)) {
        setEmailError(false);
        setEmailHelpText("");
      }
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!xsrfToken) return;

    if (!captcha) {
      setCaptchaHelpText(translations.captchaEmpty);
      return;
    }

    // invalid email handler
    if (!emailUtils.isValid(formData.email)) {
      setEmailError(true);
      setEmailHelpText(
        formData.email.length === 0
          ? translations.emailEmpty
          : translations.emailInvalid,
      );
      return;
    }

    const subscriberData = {
      FirstName: formData.firstName,
      LastName: formData.lastName,
      EmailAddress: formData.email,
      UserLanguage: lang.toUpperCase(),
    };

    setLoading(true);
    const res = await subscriberServices.addSubscriber(
      subscriberData,
      xsrfToken,
    );
    setLoading(false);

    if (res.status === 200) {
      if (lang === "en") {
        navigate("/en/success");
      } else {
        navigate("/fr/reussi");
      }
      return;
    }

    if (res.status === 422) {
      setEmailError(true);
      if (res.data === "Failed to add subscriber") {
        setEmailHelpText(translations.emailDuplicate);
        return;
      }

      if (res.data === "Invalid email address") {
        setEmailHelpText(translations.emailInvalid);
        return;
      }
    }
  };

  const handleCaptcha = async (token: string | null) => {
    if (token) {
      const result = await subscriberServices.checkCaptcha(token);
      if (result) {
        setCaptchaHelpText("");
      }
      setCaptcha(result);
    } else {
      setCaptcha(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        item
        sx={{
          backgroundColor: "#fff",
          margin: "48px 0",
          width: { xs: "100%", sm: "582px" },
          borderRadius: "24px",
          padding: { xs: "24px", sm: "48px" },
          boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.16)",
        }}
      >
        <Grid container rowSpacing={6}>
          <Grid item xs={12}>
            <EqTypography variant="h3">{translations.title}</EqTypography>
          </Grid>

          <Grid item xs={12}>
            <Grid container columnSpacing={2} rowSpacing={4}>
              <Grid item xs={12} sm={6}>
                <EqQuestionBlockContainer
                  helperProps={{
                    helpText: "",
                  }}
                  innerComponentProps={{
                    name: "firstName",
                    onChange: handleInputChange,
                    value: formData.firstName,
                  }}
                  labelProps={{
                    labelText: translations.firstNameLabel,
                  }}
                  variant="input"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EqQuestionBlockContainer
                  helperProps={{
                    helpText: "",
                  }}
                  innerComponentProps={{
                    name: "lastName",
                    onChange: handleInputChange,
                    value: formData.lastName,
                  }}
                  labelProps={{
                    labelText: translations.lastNameLabel,
                  }}
                  variant="input"
                />
              </Grid>
              <Grid item xs={12}>
                <EqQuestionBlockContainer
                  error={emailError}
                  helperProps={{
                    helpText: emailHelpText,
                  }}
                  innerComponentProps={{
                    name: "email",
                    onChange: handleInputChange,
                  }}
                  labelProps={{
                    labelText: translations.emailLabel,
                    required: true,
                  }}
                  variant="input"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{
                transform: "scale(0.78)",
                transformOrigin: "0 0",
                marginBottom: "-19px",
              }}
            >
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_SITE_KEY || ""}
                size="normal"
                hl={lang === "en" ? lang : "fr-CA"}
                onChange={handleCaptcha}
              />
            </div>
            {captchaHelpText && (
              <div style={{ marginTop: "8px" }}>
                <EqHelpText helpText={captchaHelpText} error />
              </div>
            )}
          </Grid>
          <Grid item xs={12}>
            <EqButton
              label={translations.buttonLabel}
              variant="contained"
              color="surfacePrimaryDark"
              sx={{ width: "100%", padding: "16px", height: "48px" }}
              type="submit"
              loading={loading}
            />
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default Subscribe;
